import { useState, useRef, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FileText, Upload, Pen, Eraser, Check, X, Loader2, Download, FolderOpen, Save, Eye, Edit3 } from "lucide-react";
import { useTranslation } from "@/lib/i18n/useTranslation";
import { toast } from "sonner";
import { useUsageLimits } from "@/hooks/useUsageLimits";
import { UsageLimitBanner } from "@/components/usage/UsageLimitBanner";
import { UpgradePrompt } from "@/components/usage/UpgradePrompt";
import { PDFDocument } from "pdf-lib";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { Document, Page } from "react-pdf";
import { pdfOptions } from "@/lib/pdfConfig";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSavedSignatures } from "@/hooks/useSavedSignatures";
import { hapticLight, hapticMedium, hapticSuccess, hapticError } from "@/lib/despiaHaptics";

// Modular components
import { EditorToolbar } from "./EditorToolbar";
import { PDFElementRenderer } from "./PDFElementRenderer";
import { ElementContextMenu } from "./ElementContextMenu";
import { TextElementDialog } from "./TextElementDialog";
import { SavedSignaturesPicker, SaveSignatureDialog } from "./SavedSignaturesPicker";
import { 
  type PDFElement, 
  type ElementType, 
  type TextStyle,
  DEFAULT_ELEMENT_SIZES, 
  DEFAULT_TEXT_STYLE,
  formatDateWithFormat
} from "./types";

// Utility function to convert DataURL to ArrayBuffer
// More robust than fetch() for base64 data URLs
const dataUrlToArrayBuffer = (dataUrl: string): ArrayBuffer => {
  const base64 = dataUrl.split(',')[1];
  const binaryString = window.atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
};

interface Point {
  x: number;
  y: number;
}

interface PDFFile {
  file?: File;
  documentId?: string;
  name: string;
  size: number;
  filePath?: string;
}

interface PDFEditorProps {
  initialPdf?: { blob: Blob; fileName: string } | null;
  onComplete?: () => void;
}

const PDFEditor = ({ initialPdf, onComplete }: PDFEditorProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { saveSignature: saveSignatureToDb } = useSavedSignatures();
  
  // PDF state
  const [pdfFile, setPdfFile] = useState<PDFFile | null>(null);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [showPreview, setShowPreview] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(false);
  
  // Zoom state - native CSS zoom instead of TransformWrapper
  const [zoomLevel, setZoomLevel] = useState<number>(1);
  
  // Multi-element state
  const [elements, setElements] = useState<PDFElement[]>([]);
  const [activeElementId, setActiveElementId] = useState<string | null>(null);
  
  // Signature drawing state
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [pendingSignatureData, setPendingSignatureData] = useState<string | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  
  // Dialogs state
  const [showTextDialog, setShowTextDialog] = useState(false);
  const [showSignaturePicker, setShowSignaturePicker] = useState(false);
  const [showStampPicker, setShowStampPicker] = useState(false);
  const [showSaveSignatureDialog, setShowSaveSignatureDialog] = useState(false);
  
  // UI state
  const [processing, setProcessing] = useState(false);
  const [saveToDocuments, setSaveToDocuments] = useState(true);
  const [existingDocs, setExistingDocs] = useState<Tables<"documents">[]>([]);
  const [docDialogOpen, setDocDialogOpen] = useState(false);
  const [loadingDocs, setLoadingDocs] = useState(false);
  
  const { checkLimit, incrementUsage, canUseFeature } = useUsageLimits();
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const lastPointRef = useRef<Point | null>(null);
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const stampInputRef = useRef<HTMLInputElement>(null);

  // Fetch existing PDF documents
  const fetchExistingDocs = async () => {
    setLoadingDocs(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("documents")
        .select("*")
        .eq("user_id", user.id)
        .eq("mime_type", "application/pdf")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setExistingDocs(data || []);
    } catch (error) {
      console.error("Error fetching documents:", error);
    } finally {
      setLoadingDocs(false);
    }
  };

  useEffect(() => {
    fetchExistingDocs();
  }, []);

  // Load initial PDF from props (e.g., from PDFMerge)
  useEffect(() => {
    if (initialPdf) {
      if (initialPdf.blob.size === 0) {
        toast.error("PDF reçu vide, la fusion a échoué");
        return;
      }
      
      const file = new File([initialPdf.blob], initialPdf.fileName, { type: 'application/pdf' });
      setPdfFile({
        file,
        name: initialPdf.fileName,
        size: initialPdf.blob.size
      });
      setPdfBlob(initialPdf.blob);
      setElements([]);
      
      const url = URL.createObjectURL(initialPdf.blob);
      setPdfUrl(url);
      setShowPreview(true);
    }
  }, [initialPdf]);

  // Fit-to-Width zoom calculation on document load
  const onDocumentLoadSuccess = ({ numPages: pages }: { numPages: number }) => {
    setNumPages(pages);
    setCurrentPage(1);
    
    // Calculate fit-to-width scale
    if (scrollContainerRef.current) {
      const containerWidth = scrollContainerRef.current.clientWidth;
      const padding = 32;
      const pdfNativeWidth = 612; // Standard A4 width in points
      const fitScale = (containerWidth - padding) / pdfNativeWidth;
      setZoomLevel(Math.min(Math.max(fitScale, 0.5), 1.5));
    }
  };

  // Load PDF for preview
  const loadPdfForPreview = async () => {
    if (!pdfFile) return;
    
    setLoadingPdf(true);
    try {
      let blob: Blob;
      if (pdfFile.file) {
        blob = pdfFile.file;
      } else if (pdfFile.filePath) {
        const { data, error } = await supabase.storage
          .from('documents')
          .download(pdfFile.filePath);
        if (error) throw error;
        blob = data;
      } else {
        throw new Error("Fichier non trouvé");
      }
      
      setPdfBlob(blob);
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      setShowPreview(true);
    } catch (error) {
      console.error("Error loading PDF:", error);
      toast.error("Erreur lors du chargement du PDF");
    } finally {
      setLoadingPdf(false);
    }
  };

  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setPdfFile({
        file: selectedFile,
        name: selectedFile.name,
        size: selectedFile.size
      });
      setElements([]);
      setPdfBlob(null);
      setPdfUrl(null);
      setShowPreview(false);
    } else {
      toast.error("Veuillez sélectionner un fichier PDF");
    }
    e.target.value = '';
  }, []);

  const selectExistingDoc = (doc: Tables<"documents">) => {
    setPdfFile({
      documentId: doc.id,
      name: doc.file_name,
      size: doc.file_size || 0,
      filePath: doc.file_path
    });
    setElements([]);
    setPdfBlob(null);
    setPdfUrl(null);
    setShowPreview(false);
    setDocDialogOpen(false);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' Ko';
    return (bytes / (1024 * 1024)).toFixed(1) + ' Mo';
  };

  // Zoom controls
  const handleZoomIn = () => setZoomLevel(prev => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
  const handleResetZoom = () => {
    if (scrollContainerRef.current) {
      const containerWidth = scrollContainerRef.current.clientWidth;
      const padding = 32;
      const pdfNativeWidth = 612;
      const fitScale = (containerWidth - padding) / pdfNativeWidth;
      setZoomLevel(Math.min(Math.max(fitScale, 0.5), 1.5));
    }
  };

  // Canvas drawing - transparent background
  useEffect(() => {
    if (showSignaturePad && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        // Clear with transparent background (NOT white fill)
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.strokeStyle = '#1a1a2e';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
      }
    }
  }, [showSignaturePad]);

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    hapticLight(); // Feedback tactile au début du dessin
    setIsDrawing(true);
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const point = getPoint(e, rect);
    lastPointRef.current = point;
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx || !lastPointRef.current) return;

    const rect = canvas.getBoundingClientRect();
    const point = getPoint(e, rect);

    ctx.beginPath();
    ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    ctx.lineTo(point.x, point.y);
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();

    lastPointRef.current = point;
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    lastPointRef.current = null;
  };

  const getPoint = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>, rect: DOMRect): Point => {
    const canvas = canvasRef.current!;
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if ('touches' in e) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) {
      // Clear with transparent background (not white)
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  const confirmSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const data = canvas.toDataURL('image/png');
      setPendingSignatureData(data);
      addElement('signature', data);
      setShowSignaturePad(false);
      toast.success("Signature ajoutée");
    }
  };

  // Element management
  const addElement = (type: ElementType, content: string, style?: TextStyle) => {
    const size = DEFAULT_ELEMENT_SIZES[type];
    const newElement: PDFElement = {
      id: crypto.randomUUID(),
      type,
      x: 100,
      y: 100,
      width: size.width,
      height: size.height,
      page: currentPage,
      content,
      style: style || (type === 'text' || type === 'date' ? DEFAULT_TEXT_STYLE : undefined),
    };
    setElements(prev => [...prev, newElement]);
    setActiveElementId(newElement.id);
    hapticMedium(); // Feedback tactile - élément ajouté
  };

  const updateElement = (id: string, updates: Partial<PDFElement>) => {
    setElements(prev => prev.map(el => 
      el.id === id ? { ...el, ...updates } : el
    ));
  };

  const deleteElement = (id: string) => {
    setElements(prev => prev.filter(el => el.id !== id));
    if (activeElementId === id) {
      setActiveElementId(null);
    }
  };

  const handleTextChange = (id: string, text: string) => {
    updateElement(id, { content: text });
  };

  // Toolbar actions
  const handleAddSignature = () => {
    hapticLight(); // Feedback tactile - sélection d'outil
    setShowSignaturePicker(true);
  };

  const handleAddText = () => {
    hapticLight(); // Feedback tactile - sélection d'outil
    setShowTextDialog(true);
  };

  const handleAddDate = () => {
    hapticLight(); // Feedback tactile - sélection d'outil
    const today = formatDateWithFormat(new Date(), 'dd/MM/yyyy');
    addElement('date', today, { ...DEFAULT_TEXT_STYLE, dateFormat: 'dd/MM/yyyy' });
    toast.success("Date ajoutée");
  };

  const handleAddStamp = () => {
    hapticLight(); // Feedback tactile - sélection d'outil
    setShowStampPicker(true);
  };

  const handleTextConfirm = (text: string, style: TextStyle) => {
    addElement('text', text, style);
    toast.success("Texte ajouté");
  };

  const handleSignatureSelect = (dataUrl: string) => {
    addElement('signature', dataUrl);
    toast.success("Signature ajoutée");
  };

  const handleStampSelect = (dataUrl: string) => {
    addElement('stamp', dataUrl);
    toast.success("Tampon ajouté");
  };

  const handleStampUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target?.result as string;
        addElement('stamp', dataUrl);
        toast.success("Tampon ajouté");
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  // Context menu handlers
  const activeElement = elements.find(el => el.id === activeElementId);
  const showContextMenu = activeElement && (activeElement.type === 'text' || activeElement.type === 'date');

  const handleUpdateElementStyle = (updates: Partial<TextStyle>) => {
    if (activeElementId && activeElement) {
      updateElement(activeElementId, {
        style: { ...activeElement.style, ...updates } as TextStyle
      });
    }
  };

  const handleUpdateElementContent = (content: string) => {
    if (activeElementId) {
      updateElement(activeElementId, { content });
    }
  };

  // Apply all elements to PDF
  const handleApplyElements = async () => {
    if (!pdfFile || elements.length === 0) {
      hapticError(); // Feedback tactile - erreur
      toast.error("Ajoutez au moins un élément (signature, texte, tampon)");
      return;
    }
    
    if (!checkLimit("pdf_actions")) {
      hapticError(); // Feedback tactile - erreur
      toast.error("Limite d'actions PDF atteinte. Passez à un plan supérieur.");
      return;
    }
    
    setProcessing(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      // Get PDF bytes
      let pdfBytes: ArrayBuffer;
      if (pdfFile.file) {
        pdfBytes = await pdfFile.file.arrayBuffer();
      } else if (pdfFile.filePath) {
        const { data, error } = await supabase.storage
          .from('documents')
          .download(pdfFile.filePath);
        if (error) throw error;
        pdfBytes = await data.arrayBuffer();
      } else {
        throw new Error("Fichier non trouvé");
      }

      const pdfDoc = await PDFDocument.load(pdfBytes);
      const pages = pdfDoc.getPages();
      
      const containerWidth = previewContainerRef.current?.clientWidth || 400;
      const containerHeight = previewContainerRef.current?.clientHeight || 600;

      // Apply each element
      for (const element of elements) {
        const targetPageIndex = element.page - 1;
        const targetPage = pages[targetPageIndex] || pages[0];
        const { width: pdfWidth, height: pdfHeight } = targetPage.getSize();
        
        // Account for zoom level in scaling
        const scaleX = pdfWidth / (containerWidth * zoomLevel);
        const scaleY = pdfHeight / (containerHeight * zoomLevel);

        if (element.type === 'signature' || element.type === 'stamp') {
          try {
            // Use robust DataURL conversion instead of fetch()
            const imageArrayBuffer = dataUrlToArrayBuffer(element.content);
            
            // Detect format from DataURL prefix
            const isPng = element.content.startsWith('data:image/png');
            
            let image;
            try {
              image = isPng 
                ? await pdfDoc.embedPng(imageArrayBuffer)
                : await pdfDoc.embedJpg(imageArrayBuffer);
            } catch (embedError) {
              console.warn('[PDFEditor] Primary embed failed, trying fallback:', embedError);
              // Fallback: try the other format
              image = isPng 
                ? await pdfDoc.embedJpg(imageArrayBuffer)
                : await pdfDoc.embedPng(imageArrayBuffer);
            }
            
            const elementWidth = element.width * scaleX;
            const elementHeight = element.height * scaleY;
            const xPos = element.x * scaleX;
            const yPos = pdfHeight - (element.y * scaleY) - elementHeight;
            
            // Validate coordinates before drawing
            if (isNaN(xPos) || isNaN(yPos) || isNaN(elementWidth) || isNaN(elementHeight)) {
              console.error('[PDFEditor] Invalid coordinates for element:', element);
              continue;
            }
            
            targetPage.drawImage(image, {
              x: xPos,
              y: yPos,
              width: elementWidth,
              height: elementHeight,
            });
          } catch (imgError) {
            console.error('[PDFEditor] Failed to embed image:', imgError);
            toast.error(`Impossible d'intégrer l'image: ${element.type}`);
            continue;
          }
        } else if (element.type === 'text' || element.type === 'date') {
          const fontSize = (element.style?.fontSize || 16) * scaleX;
          const xPos = element.x * scaleX;
          const yPos = pdfHeight - (element.y * scaleY) - fontSize;
          
          // Validate coordinates before drawing
          if (isNaN(xPos) || isNaN(yPos) || isNaN(fontSize)) {
            console.error('[PDFEditor] Invalid text coordinates:', element);
            continue;
          }
          
          targetPage.drawText(element.content, {
            x: xPos,
            y: yPos,
            size: fontSize,
          });
        }
      }
      
      const modifiedPdfBytes = await pdfDoc.save();
      const blob = new Blob([new Uint8Array(modifiedPdfBytes).buffer as ArrayBuffer], { type: 'application/pdf' });
      const fileName = `${pdfFile.name.replace('.pdf', '')}-edite.pdf`;

      if (saveToDocuments) {
        const filePath = `${user.id}/${crypto.randomUUID()}.pdf`;
        const { error: uploadError } = await supabase.storage
          .from('documents')
          .upload(filePath, blob);

        if (uploadError) throw uploadError;

        const { data: insertedDoc, error: insertError } = await supabase
          .from('documents')
          .insert({
            user_id: user.id,
            title: 'Document édité',
            file_name: fileName,
            file_path: filePath,
            file_size: blob.size,
            mime_type: 'application/pdf',
            source: 'upload',
            status: 'processing',
            doc_type: 'other'
          })
          .select()
          .single();

        if (insertError) throw insertError;

        // Trigger AI analysis
        try {
          const { data: analysisData } = await supabase.functions.invoke('extract-document-info', {
            body: {
              fileName: fileName,
              mimeType: 'application/pdf',
              docType: 'other',
              filePath: filePath
            }
          });

          if (analysisData) {
            const updates: Record<string, any> = {
              status: 'complete',
              processed_by_pipeline: true,
              updated_at: new Date().toISOString()
            };

            if (analysisData.suggested_title) updates.title = analysisData.suggested_title;
            if (analysisData.doc_type) updates.doc_type = analysisData.doc_type;
            if (analysisData.category) updates.category = analysisData.category;
            if (analysisData.summary) updates.ai_summary = analysisData.summary;
            if (analysisData.doc_date) updates.doc_date = analysisData.doc_date;
            if (analysisData.expires_at !== undefined) updates.expires_at = analysisData.expires_at;
            if (analysisData.extracted_text) updates.extracted_text = analysisData.extracted_text;

            await supabase.from('documents').update(updates).eq('id', insertedDoc.id);
          }
        } catch (analysisError) {
          console.warn('AI analysis failed:', analysisError);
          await supabase.from('documents').update({ 
            status: 'complete',
            processed_by_pipeline: true
          }).eq('id', insertedDoc.id);
        }

        hapticSuccess(); // Feedback tactile - succès
        toast.success("Document édité et sauvegardé !");
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        hapticSuccess(); // Feedback tactile - succès
        toast.success("Document édité téléchargé !");
      }

      await incrementUsage("pdf_actions");
      
      // Reset state
      setPdfFile(null);
      setPdfBlob(null);
      setPdfUrl(null);
      setShowPreview(false);
      setElements([]);
      setActiveElementId(null);
      fetchExistingDocs();
      
      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      console.error('Error applying elements:', error);
      hapticError(); // Feedback tactile - erreur
      toast.error("Erreur lors de l'édition du PDF");
    } finally {
      setProcessing(false);
    }
  };

  // Get elements for current page
  const currentPageElements = elements.filter(el => el.page === currentPage);
  const hasElements = elements.length > 0;

  return (
    <Card className={isMobile ? 'pb-20' : ''}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Edit3 className="w-5 h-5" />
          Éditer un PDF
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Ajoutez signatures, textes, dates et tampons à vos documents
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {!canUseFeature("sign") ? (
          <UpgradePrompt 
            feature="Édition de documents" 
            description="Éditez vos documents PDF directement dans EasyFinder. Disponible avec un abonnement payant."
            minPlan="Perso"
          />
        ) : (
          <>
            <UsageLimitBanner type="pdf_actions" showUpgrade={false} />
        
            {!pdfFile ? (
              <>
                <div className="flex gap-2">
                  <div 
                    className="flex-1 border-2 border-dashed border-border rounded-xl p-6 text-center hover:border-primary/50 hover:bg-primary/5 transition-colors cursor-pointer"
                    onClick={() => document.getElementById('pdf-editor-input')?.click()}
                    onKeyDown={(e) => e.key === 'Enter' && document.getElementById('pdf-editor-input')?.click()}
                    role="button"
                    tabIndex={0}
                  >
                    <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm font-medium">Depuis l'appareil</p>
                    <p className="text-xs text-muted-foreground">Importer un fichier</p>
                  </div>
                  
                  <Dialog open={docDialogOpen} onOpenChange={setDocDialogOpen}>
                    <DialogTrigger asChild>
                      <div 
                        className="flex-1 border-2 border-dashed border-border rounded-xl p-6 text-center hover:border-primary/50 hover:bg-primary/5 transition-colors cursor-pointer"
                        role="button"
                        tabIndex={0}
                      >
                        <FolderOpen className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                        <p className="text-sm font-medium">Mes documents</p>
                        <p className="text-xs text-muted-foreground">Sélectionner existant</p>
                      </div>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg">
                      <DialogHeader>
                        <DialogTitle>Sélectionner un document PDF</DialogTitle>
                      </DialogHeader>
                      {loadingDocs ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="w-6 h-6 animate-spin" />
                        </div>
                      ) : existingDocs.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                          Aucun document PDF trouvé
                        </div>
                      ) : (
                        <ScrollArea className="max-h-[300px]">
                          <div className="space-y-2">
                            {existingDocs.map(doc => (
                              <div 
                                key={doc.id}
                                className="flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors hover:bg-secondary/50"
                                onClick={() => selectExistingDoc(doc)}
                              >
                                <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center">
                                  <FileText className="w-4 h-4 text-primary" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium truncate">{doc.file_name}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {formatFileSize(doc.file_size || 0)}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      )}
                    </DialogContent>
                  </Dialog>
                </div>
                <input
                  id="pdf-editor-input"
                  type="file"
                  accept="application/pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </>
            ) : (
              <div className="space-y-4">
                {/* Selected file info */}
                <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary/30">
                  <div className="w-10 h-10 rounded bg-primary/10 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">{pdfFile.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(pdfFile.size)}
                      {pdfFile.documentId && <span className="ml-2 text-primary">• EasyFinder</span>}
                    </p>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => { 
                    setPdfFile(null); 
                    setPdfBlob(null); 
                    setPdfUrl(null); 
                    setShowPreview(false); 
                    setElements([]); 
                  }}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>

                {/* Load preview button */}
                {!showPreview && (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={loadPdfForPreview}
                    disabled={loadingPdf}
                  >
                    {loadingPdf ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Eye className="w-4 h-4 mr-2" />
                    )}
                    Ouvrir l'éditeur
                  </Button>
                )}

                {/* PDF Editor - Native scroll */}
                {showPreview && pdfUrl && (
                  <div className="space-y-3 border border-border rounded-xl p-4 bg-secondary/20">
                    <EditorToolbar
                      currentPage={currentPage}
                      numPages={numPages}
                      zoomLevel={zoomLevel}
                      onPageChange={setCurrentPage}
                      onAddSignature={handleAddSignature}
                      onAddText={handleAddText}
                      onAddDate={handleAddDate}
                      onAddStamp={handleAddStamp}
                      onZoomIn={handleZoomIn}
                      onZoomOut={handleZoomOut}
                      onResetZoom={handleResetZoom}
                    />
                    
                    {/* Scrollable container - native scroll */}
                    <div 
                      ref={scrollContainerRef}
                      className="relative overflow-y-auto overflow-x-hidden rounded-lg bg-muted/30"
                      style={{ maxHeight: isMobile ? '60vh' : '70vh' }}
                    >
                      {/* Zoom wrapper */}
                      <div 
                        style={{ 
                          transform: `scale(${zoomLevel})`,
                          transformOrigin: 'top center',
                          width: `${100 / zoomLevel}%`,
                          margin: '0 auto'
                        }}
                      >
                        <div 
                          ref={previewContainerRef}
                          className="relative rounded-lg overflow-hidden bg-white shadow-inner mx-auto"
                          style={{ minHeight: "400px" }}
                          onClick={() => setActiveElementId(null)}
                        >
                          <Document
                            file={pdfBlob || pdfUrl}
                            onLoadSuccess={onDocumentLoadSuccess}
                            onLoadError={(error) => {
                              console.error('[PDFEditor] Document load error:', error);
                              toast.error("Échec du chargement du PDF");
                            }}
                            loading={
                              <div className="flex items-center justify-center h-[400px]">
                                <Loader2 className="w-6 h-6 animate-spin" />
                              </div>
                            }
                            {...pdfOptions}
                          >
                            <Page 
                              pageNumber={currentPage} 
                              width={previewContainerRef.current?.clientWidth || 400}
                              renderTextLayer={false}
                              renderAnnotationLayer={false}
                            />
                          </Document>
                          
                          {/* Render elements for current page */}
                          {currentPageElements.map((element) => (
                            <PDFElementRenderer
                              key={element.id}
                              element={element}
                              isActive={activeElementId === element.id}
                              isEditable={true}
                              onSelect={setActiveElementId}
                              onUpdate={updateElement}
                              onDelete={deleteElement}
                              onTextChange={handleTextChange}
                            />
                          ))}
                          
                          {/* Context menu for text/date elements */}
                          {showContextMenu && activeElement && (
                            <ElementContextMenu
                              element={activeElement}
                              position={{ x: activeElement.x, y: activeElement.y }}
                              containerRect={previewContainerRef.current?.getBoundingClientRect() || null}
                              onUpdateStyle={handleUpdateElementStyle}
                              onUpdateContent={handleUpdateElementContent}
                              onDelete={() => deleteElement(activeElement.id)}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Status bar */}
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Edit3 className="w-3 h-3" />
                      <span>Cliquez sur les outils pour ajouter des éléments • Faites défiler pour naviguer</span>
                    </div>

                    {/* Elements count */}
                    {hasElements && (
                      <div className="flex items-center justify-between text-xs text-muted-foreground border-t border-border pt-2">
                        <span>{elements.length} élément(s) sur {new Set(elements.map(e => e.page)).size} page(s)</span>
                        {pendingSignatureData && (
                          <Button
                            variant="link"
                            size="sm"
                            className="h-auto p-0 text-xs"
                            onClick={() => setShowSaveSignatureDialog(true)}
                          >
                            <Save className="w-3 h-3 mr-1" />
                            Sauvegarder la signature
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Signature Pad Dialog */}
                {showSignaturePad && (
                  <div className="space-y-4 p-4 border border-border rounded-xl bg-card">
                    <p className="text-sm font-medium">{t.pdf.drawSignature}</p>
                    <div className="border border-border rounded-lg overflow-hidden">
                      <canvas
                        ref={canvasRef}
                        width={400}
                        height={150}
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        onTouchStart={startDrawing}
                        onTouchMove={draw}
                        onTouchEnd={stopDrawing}
                        className="w-full touch-none cursor-crosshair"
                        style={{
                          // Checkerboard pattern to indicate transparency (like Photoshop)
                          backgroundImage: `
                            linear-gradient(45deg, #e0e0e0 25%, transparent 25%),
                            linear-gradient(-45deg, #e0e0e0 25%, transparent 25%),
                            linear-gradient(45deg, transparent 75%, #e0e0e0 75%),
                            linear-gradient(-45deg, transparent 75%, #e0e0e0 75%)
                          `,
                          backgroundSize: '16px 16px',
                          backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px',
                          backgroundColor: '#f5f5f5'
                        }}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" onClick={clearSignature}>
                        <Eraser className="w-4 h-4 mr-2" />
                        {t.pdf.clearSignature}
                      </Button>
                      <Button variant="outline" onClick={() => setShowSignaturePad(false)}>
                        <X className="w-4 h-4 mr-2" />
                        Annuler
                      </Button>
                      <Button onClick={confirmSignature}>
                        <Check className="w-4 h-4 mr-2" />
                        Ajouter
                      </Button>
                    </div>
                  </div>
                )}

                {/* Save options and Apply button */}
                {hasElements && !showSignaturePad && (
                  <div className="space-y-3 pt-2 border-t border-border">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="saveToDocuments"
                        checked={saveToDocuments}
                        onCheckedChange={(checked) => setSaveToDocuments(!!checked)}
                      />
                      <label htmlFor="saveToDocuments" className="text-sm cursor-pointer">
                        Sauvegarder dans mes documents
                      </label>
                    </div>
                    
                    <Button 
                      onClick={handleApplyElements} 
                      disabled={processing}
                      className="w-full"
                    >
                      {processing ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : saveToDocuments ? (
                        <Save className="w-4 h-4 mr-2" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      {saveToDocuments ? "Appliquer et sauvegarder" : "Appliquer et télécharger"}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Dialogs */}
        <TextElementDialog
          open={showTextDialog}
          onOpenChange={setShowTextDialog}
          onConfirm={handleTextConfirm}
        />

        <SavedSignaturesPicker
          open={showSignaturePicker}
          onOpenChange={setShowSignaturePicker}
          type="signature"
          onSelect={handleSignatureSelect}
          onCreateNew={() => {
            setShowSignaturePicker(false);
            setShowSignaturePad(true);
          }}
        />

        <SavedSignaturesPicker
          open={showStampPicker}
          onOpenChange={setShowStampPicker}
          type="stamp"
          onSelect={handleStampSelect}
          onCreateNew={() => {
            setShowStampPicker(false);
            stampInputRef.current?.click();
          }}
        />

        <SaveSignatureDialog
          open={showSaveSignatureDialog}
          onOpenChange={setShowSaveSignatureDialog}
          signatureDataUrl={pendingSignatureData || ''}
          type="signature"
          onSave={async (name) => {
            if (pendingSignatureData) {
              await saveSignatureToDb(pendingSignatureData, 'signature', name);
              setPendingSignatureData(null);
            }
          }}
        />

        {/* Hidden stamp upload input */}
        <input
          ref={stampInputRef}
          type="file"
          accept="image/*"
          onChange={handleStampUpload}
          className="hidden"
        />
      </CardContent>
    </Card>
  );
};

export default PDFEditor;
